<template>
  <div class="w-full">
    <!-- Monitoring Status -->
    <div v-if="monitor.isMonitoring.value" class="flex flex-col gap-4 p-6 bg-blue-500/5 border-2 border-blue-500/20 rounded-xl sm:p-5">
      <div class="flex items-center gap-4">
        <div class="spinner"></div>
        <h3 class="m-0 text-lg font-semibold text-vault-text-primary">{{ $t('depositWidget.changelly.monitoring') }}</h3>
      </div>

      <div class="flex flex-col gap-3">
        <p class="m-0 text-sm text-vault-text-secondary">
          Checking balance every {{ pollInterval / 1000 }} seconds...
        </p>

        <!-- Progress Bar -->
        <div class="w-full h-1.5 bg-blue-500/10 rounded-sm overflow-hidden">
          <div class="h-full bg-gradient-to-r from-blue-500/80 to-blue-500 rounded-sm transition-all duration-300" :style="{ width: `${monitor.progress.value}%` }"></div>
        </div>

        <p class="m-0 text-sm font-semibold text-blue-500/90">
          Elapsed: {{ formatTime(monitor.elapsedTime.value) }}
        </p>

        <!-- Current Balance -->
        <div v-if="monitor.currentBalance.value" class="flex justify-between items-center p-3 bg-white/60 rounded-lg">
          <span class="text-[13px] text-vault-text-secondary font-medium">Current Balance:</span>
          <span class="text-base font-bold text-vault-text-primary">
            {{ formatBalance(monitor.currentBalance.value.balanceEth) }} {{ currency }}
          </span>
        </div>
      </div>

      <button type="button" class="btn-ghost !px-4 !py-2 !text-[13px]" @click="handleStop">
        Stop Monitoring
      </button>
    </div>

    <!-- Completed Status -->
    <div v-if="monitor.isCompleted.value" class="flex flex-col items-center gap-3 p-8 bg-green-500/5 border-2 border-green-500/30 rounded-xl text-center sm:p-5">
      <div class="text-green-500/90">
        <CheckCircle :size="48" />
      </div>
      <h3 class="m-0 text-xl font-semibold text-vault-text-primary">Deposit Received!</h3>
      <p class="m-0 text-2xl font-bold text-green-500/90">
        {{ formatBalance(monitor.currentBalance.value?.balanceEth || '0') }} {{ currency }}
      </p>
    </div>

    <!-- Timeout Status -->
    <div v-if="monitor.isTimedOut.value" class="flex flex-col items-center gap-3 p-8 bg-orange-500/5 border-2 border-orange-500/30 rounded-xl text-center sm:p-5">
      <div class="text-orange-500/90">
        <Clock :size="48" />
      </div>
      <h3 class="m-0 text-xl font-semibold text-vault-text-primary">Monitoring Timeout</h3>
      <p class="m-0 text-sm text-vault-text-secondary leading-relaxed">
        We stopped checking for deposits after {{ formatTime(timeout) }}.
      </p>
      <p class="m-0 text-[13px] text-vault-text-secondary italic leading-relaxed">
        Don't worry - your payment may still arrive. Check balance manually below.
      </p>
      <button type="button" class="btn-primary !px-5 !py-2.5 !text-sm" @click="handleRefresh">
        Check Balance Now
      </button>
    </div>

    <!-- Error Status -->
    <div v-if="monitor.hasError.value" class="flex flex-col items-center gap-3 p-8 bg-red-500/5 border-2 border-red-500/30 rounded-xl text-center sm:p-5">
      <div class="text-red-500/90">
        <AlertCircle :size="48" />
      </div>
      <h3 class="m-0 text-xl font-semibold text-vault-text-primary">Monitoring Error</h3>
      <p class="m-0 text-sm text-red-500/80 leading-relaxed">{{ monitor.error.value?.message || 'Unknown error' }}</p>
      <button type="button" class="btn-primary !px-5 !py-2.5 !text-sm" @click="handleRetry">
        Retry
      </button>
    </div>
  </div>
</template>

<script setup>
import { CheckCircle, Clock, AlertCircle } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useDepositMonitor } from '../../composables/useDepositMonitor'

const { t: $t } = useI18n()

const props = defineProps({
  address: {
    type: String,
    required: true
  },
  currency: {
    type: String,
    default: 'USDC'
  },
  currencyInfo: {
    type: Object,
    default: null
  },
  rpcUrl: {
    type: String,
    default: ''
  },
  chain: {
    type: String,
    default: 'base_sepolia'
  },
  pollInterval: {
    type: Number,
    default: 10000 // 10 seconds
  },
  timeout: {
    type: Number,
    default: 900000 // 15 minutes
  },
  autoStart: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['deposit-received', 'timeout', 'error'])

// Initialize deposit monitor
const monitor = useDepositMonitor({
  address: props.address,
  rpcUrl: props.rpcUrl,
  currency: props.currencyInfo,
  chain: props.chain,
  pollInterval: props.pollInterval,
  timeout: props.timeout,
  minBalance: 0n, // Any balance change counts as deposit
  onDepositReceived: (balance) => {
    emit('deposit-received', balance)
  },
  onTimeout: () => {
    emit('timeout')
  },
  onError: (error) => {
    emit('error', error)
  }
})

// Auto-start monitoring if enabled
if (props.autoStart) {
  monitor.startMonitoring()
}

// Format time (milliseconds to mm:ss)
const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// Format balance
const formatBalance = (balance) => {
  const num = parseFloat(balance)
  if (num === 0) return '0'
  if (num < 0.0001) return '<0.0001'
  return num.toFixed(4)
}

// Handlers
const handleStop = () => {
  monitor.stopMonitoring()
}

const handleRefresh = async () => {
  await monitor.refresh()
}

const handleRetry = async () => {
  monitor.reset()
  await monitor.startMonitoring()
}

// Expose methods to parent
defineExpose({
  start: monitor.startMonitoring,
  stop: monitor.stopMonitoring,
  reset: monitor.reset,
  refresh: monitor.refresh
})
</script>

<style scoped>
/* Loading Spinner */
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(33, 150, 243, 0.2);
  border-top-color: rgba(33, 150, 243, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
